<template>
  <div
    v-if="computedUpsellItems.length > 0"
    class="cf-flex cf-flex--column cf-flex--gap-8"
  >
    <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-space-between">
      <h4 class="cf-title cf-title--4">
        {{ upsellContent }}
      </h4>

      <div
        v-if="computedUpsellItems.length > 1"
        class="cf-flex cf-flex--row cf-flex--gap-12"
      >
        <button
          class="cf-button cf-button--secondary cf-button--fixed-32"
          type="button"
          title="Scroll to previous item"
          @click="scrollHandler('previous')"
        >
          <span class="cf-icon far fa-arrow-left" />
        </button>

        <button
          class="cf-button cf-button--secondary cf-button--fixed-32"
          type="button"
          title="Scroll to next item"
          @click="scrollHandler('next')"
        >
          <span class="cf-icon far fa-arrow-right" />
        </button>
      </div>
    </div>

    <div
      ref="carousel"
      :class="['cf-menu-list__carousel', { 'cf-menu-list__carousel--single-item': computedUpsellItems.length === 1 }]"
    >
      <div class="cf-menu-list__items">
        <cf-menu-list-item
          v-for="item in computedUpsellItems"
          :key="item.menuItemId"
          ref="item"
          :item="item"
          :category-id="item.menuCategoryId"
          :is-menu-view="false"
        />
      </div>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import CfMenuListItem from '@/view/components/globals/cf-menu-list/cf-menu-list-item/index.vue';


  export default {
    name: 'CfUpsellOrderItems',

    components: {
      CfMenuListItem
    },

    // TEMP DIAGNOSTIC state — remove alongside reportUpsellDropDiagnostic().
    data() {
      return {
        reportedUpsellDropSignatures: []
      };
    },

    computed: {
      ...mapGetters('orders', ['upsellItems']),
      ...mapGetters('content', ['content']),

      upsellContent() {
        return this.content.OrderSummary.metadata.upsell;
      },

      computedUpsellItems() {
        return this.upsellItems.checkoutUpsellItems;
      }
    },

    watch: {
      computedUpsellItems: 'upsellItemsChangeHandler'
    },

    // TEMP DIAGNOSTIC — remove alongside reportUpsellDropDiagnostic().
    mounted() {
      this.reportUpsellDropDiagnostic();
    },

    methods: {
      scrollHandler(direction) {
        /*
         * Note: Not unit tests due to complexity.
         */

        const { carousel } = this.$refs;

        const itemWidth = this.$refs.item[0].$el.clientWidth;

        const scrollDistance = itemWidth;

        if (direction === 'previous') {
          if (carousel.scrollLeft === 0) {
            carousel.scrollTo({
              left: carousel.scrollWidth,
              behavior: 'smooth'
            });
          }

          else {
            carousel.scrollBy({
              left: scrollDistance * -1,
              behavior: 'smooth'
            });
          }
        }

        else if (carousel.scrollWidth - carousel.scrollLeft === carousel.clientWidth) {
          carousel.scrollTo({
            left: 0,
            behavior: 'smooth'
          });
        }

        else {
          carousel.scrollBy({
            left: scrollDistance,
            behavior: 'smooth'
          });
        }
      },

      async upsellItemsChangeHandler() {
        await this.$nextTick();

        if (this.$refs.menuCarousel) {
          this.$refs.menuCarousel.scrollLeft = 0;
        }

        this.reportUpsellDropDiagnostic();
      },

      // TEMP DIAGNOSTIC — reports to New Relic (event 'UpsellItemsDropped') when the /upsell API
      // returned items that were not rendered on checkout because they weren't found in the loaded
      // menu (see parse-upsell-items.js). Fires once per settled drop. Remove once the production
      // upsell drop is diagnosed.
      reportUpsellDropDiagnostic() {
        try {
          if (this.$store.state.menu.loadStatus !== 'loaded') {
            return;
          }

          const { order } = this.$store.state.orders;
          const orderUpsellIds = (order.upsellItems || []).map(item => item.menuItemId);
          const renderedIds = this.computedUpsellItems.map(item => item.menuItemId);
          const missingIds = orderUpsellIds.filter(id => !renderedIds.includes(id));

          if (!missingIds.length) {
            return;
          }

          const signature = `${order.orderId}:${missingIds.join(',')}`;

          if (this.reportedUpsellDropSignatures.includes(signature)) {
            return;
          }

          this.reportedUpsellDropSignatures.push(signature);

          const menu = this.$store.getters['menu/menu'];
          const unfilteredMenuItemIds = new Set(menu.categories.flatMap(category => category.menuItems.map(item => item.menuItemId)));
          const { menuRequestDetails } = this.$store.state.menu;
          const session = this.$store.getters['session/session'];
          const currentUser = this.$store.getters['session/currentUser'];

          // present in the full menu -> a filter dropped it; absent from the full menu -> the loaded menu is incomplete
          const eventData = JSON.stringify({
            orderUpsellIds,
            renderedIds,
            missingIds,
            missingButInUnfilteredMenu: missingIds.filter(id => unfilteredMenuItemIds.has(id)),
            missingFromEntireMenu: missingIds.filter(id => !unfilteredMenuItemIds.has(id)),
            menuType: menuRequestDetails.menuType,
            menuVerbosity: menuRequestDetails.menuVerbosity || null,
            filteredCategoryCount: menu.filteredCategories.length,
            allCategoryCount: menu.categories.length,
            isSignedIn: session.isSignedIn,
            isEnrolledInLoyalty: currentUser.isEnrolledInLoyalty
          });

          this.$triggerNewRelicEvent('UpsellItemsDropped', 'Information', 'Upsell items from /upsell API were not rendered on checkout', eventData);
        }
        catch (error) {
          // A diagnostic must never break the page.
        }
      }
    }
  };
</script>
